import React, { useCallback, useEffect, useState } from 'react';
import { Message } from '@/components/brand/icons/Message';
import useCreateLeads from '@/utils/useCreateLeads';

interface FloatingContactButtonProps {
  onAutoTrigger?: () => void;
}

const ListsyncFloatingContactButton: React.FC<
  FloatingContactButtonProps
> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const { onSubmit } = useCreateLeads();

  // Show tooltip for 5 seconds, then hide for 5 seconds, repeat
  useEffect(() => {
    if (isModalOpen) return; // Don't show tooltip when modal is open

    const showTooltip = () => {
      setShowTooltip(true);
      // Hide after 5 seconds
      setTimeout(() => {
        setShowTooltip(false);
        // Show again after 5 seconds of hiding
        setTimeout(() => {
          if (!isModalOpen) {
            showTooltip(); // Recursive call to repeat the cycle
          }
        }, 5000);
      }, 5000);
    };

    // Start the cycle
    showTooltip();

    // Cleanup function
    return () => {
      setShowTooltip(false);
    };
  }, [isModalOpen]);

  const handleButtonClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleInputChange = useCallback(
    (
      event:
        | React.FormEvent<HTMLInputElement>
        | React.FormEvent<HTMLTextAreaElement>
    ) => {
      const { name, value } = event.currentTarget;
      setFormValues({ ...formValues, [name]: value });
    },
    [formValues]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      try {
        onSubmit(formValues);
        setFormValues({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setIsModalOpen(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error submitting form:', error);
      }
    },
    [onSubmit, formValues]
  );

  return (
    <>
      {/* Floating Button with tooltip */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          left: '24px',
          zIndex: 50
        }}
      >
        <button
          onClick={handleButtonClick}
          style={{
            width: '56px',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)',
            border: 'none',
            boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            color: 'white'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
            e.currentTarget.style.boxShadow =
              '0 20px 25px -5px rgba(79, 70, 229, 0.5)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow =
              '0 10px 25px -5px rgba(79, 70, 229, 0.4)';
          }}
          aria-label="Contact us"
        >
          <Message className="h-6 w-6 text-white" />
        </button>

        {/* Simple text tooltip on the button */}
        {showTooltip && (
          <>
            <style
              dangerouslySetInnerHTML={{
                __html: `
                  @keyframes tooltipFadeInOut {
                    0%, 100% {
                      opacity: 0;
                      transform: translateY(4px);
                    }
                    10%, 90% {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '-48px',
                left: 0,
                animation: 'tooltipFadeInOut 5s ease-in-out infinite'
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #e2e8f0'
                }}
              >
                Click to leave a message for admin
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal - compact for small screens (e.g. 13.3" MacBook) */}
      {isModalOpen && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-3 sm:p-4">
          <div
            className="relative my-auto max-h-[88vh] w-full max-w-[min(24rem,90vw)] overflow-y-auto rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-xl sm:rounded-2xl sm:p-6"
            style={{ boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)' }}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute right-3 top-3 rounded-lg p-1 text-[#64748b] transition-colors hover:bg-[#f1f5f9] hover:text-[#1a1a1a] sm:right-4 sm:top-4"
              aria-label="Close modal"
            >
              <svg
                className="h-5 w-5 sm:h-6 sm:w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="mb-3 pr-8 sm:mb-6">
              <h3 className="text-lg font-semibold leading-tight text-[#1a1a1a] sm:text-2xl">
                Get in Touch
              </h3>
              <p className="mt-1 text-xs leading-tight text-[#64748b] sm:text-sm">
                Leave your details and we&apos;ll get back to you as soon as
                possible.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-2.5 sm:gap-4"
            >
              <div>
                <label
                  htmlFor="modal-name"
                  className="block text-sm font-medium text-[#334155]"
                >
                  Name<span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-sm text-[#1a1a1a] outline-none placeholder:text-[#94a3b8] focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] sm:px-4 sm:py-2"
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="modal-email"
                  className="block text-sm font-medium text-[#334155]"
                >
                  Email<span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-sm text-[#1a1a1a] outline-none placeholder:text-[#94a3b8] focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] sm:px-4 sm:py-2"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="modal-phone"
                  className="block text-sm font-medium text-[#334155]"
                >
                  Phone<span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="tel"
                  id="modal-phone"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-sm text-[#1a1a1a] outline-none placeholder:text-[#94a3b8] focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] sm:px-4 sm:py-2"
                  placeholder="Your phone number"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="modal-message"
                  className="block text-sm font-medium text-[#334155]"
                >
                  Message<span className="text-[#ef4444]">*</span>
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  value={formValues.message}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 w-full resize-none rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-sm leading-snug text-[#1a1a1a] outline-none placeholder:text-[#94a3b8] focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] sm:px-4 sm:py-2"
                  placeholder="How can we help you?"
                  required
                />
              </div>

              <div className="flex justify-center pt-2 sm:pt-4">
                <button
                  type="submit"
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl px-6 py-2 text-sm font-medium text-white shadow-lg transition-all hover:shadow-xl sm:w-auto sm:px-8 sm:py-2 sm:text-base"
                  style={{
                    background:
                      'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)'
                  }}
                >
                  Send Message
                  <span className="text-base">→</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ListsyncFloatingContactButton;
